import { Capacitor, registerPlugin } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

/** the native Android alarm (android/app/src/main/java/dev/luschei/lunaloo/BarkAlarm*.java) */
interface BarkAlarmPlugin {
  set(o: { hour: number; minute: number; dog: string; name: string }): Promise<{ at: number; exact: boolean }>;
  cancel(): Promise<void>;
  stop(): Promise<void>;
}
const BarkAlarm = registerPlugin<BarkAlarmPlugin>('BarkAlarm');

/**
 * The bark alarm: a daily local notification at a chosen time whose sound is her bark. Fires
 * with the app closed. Native only (the web has no way to schedule a notification while shut).
 * Android needs the sound as a raw resource (`android/app/src/main/res/raw/bark_<dog>.mp3`) on a
 * channel of its own; iOS falls back to the default sound until a .caf bark is in the bundle.
 */
const KEY = 'lunaloo.alarm';
const ID = 1;

export interface AlarmSetting {
  hour: number;
  minute: number;
  on: boolean;
}

export class Alarm {
  setting: AlarmSetting = { hour: 7, minute: 0, on: false };

  constructor(
    private dogId: string,
    private dogName: string,
  ) {
    try {
      const s = JSON.parse(localStorage.getItem(KEY) ?? 'null') as AlarmSetting | null;
      if (s) this.setting = s;
    } catch {
      /* no storage */
    }
  }

  get available(): boolean {
    return Capacitor.isNativePlatform();
  }

  private get channelId(): string {
    return `bark-${this.dogId}`;
  }

  /** save and (re)schedule; the message says what happened */
  async apply(setting: AlarmSetting): Promise<string> {
    this.setting = setting;
    localStorage.setItem(KEY, JSON.stringify(setting));
    if (!this.available) return 'alarms need the phone app';
    const hh = String(setting.hour).padStart(2, '0');
    const mm = String(setting.minute).padStart(2, '0');
    if (Capacitor.getPlatform() === 'android') {
      // the real thing: exact, on the alarm stream, through silent mode and Do Not Disturb
      try {
        if (!setting.on) {
          await BarkAlarm.cancel();
          return 'alarm off';
        }
        let perm = await LocalNotifications.checkPermissions();
        if (perm.display !== 'granted') perm = await LocalNotifications.requestPermissions();
        const r = await BarkAlarm.set({ hour: setting.hour, minute: setting.minute, dog: this.dogId, name: this.dogName });
        return `${this.dogName} will howl at ${hh}:${mm} every day${r.exact ? '' : ' (roughly — exact alarms are off for Lunaloo in Android settings)'}`;
      } catch (e) {
        return `couldn't set the alarm: ${(e as Error).message}`;
      }
    }
    try {
      await LocalNotifications.cancel({ notifications: [{ id: ID }] });
      if (!setting.on) return 'alarm off';
      let perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') perm = await LocalNotifications.requestPermissions();
      if (perm.display !== 'granted') return 'notifications are blocked for Lunaloo';
      if (Capacitor.getPlatform() === 'android') {
        await LocalNotifications.createChannel({
          id: this.channelId,
          name: `${this.dogName}'s bark`,
          description: 'the bark alarm',
          importance: 5,
          sound: `bark_${this.dogId}.mp3`,
          visibility: 1,
          vibration: true,
        });
      }
      await LocalNotifications.schedule({
        notifications: [
          {
            id: ID,
            title: `${this.dogName} says it's time`,
            body: 'woof! woof!',
            channelId: this.channelId,
            sound: `bark_${this.dogId}.mp3`,
            schedule: { on: { hour: setting.hour, minute: setting.minute }, repeats: true, allowWhileIdle: true },
          },
        ],
      });
      return `${this.dogName} will bark at ${hh}:${mm} every day`;
    } catch (e) {
      return `couldn't set the alarm: ${(e as Error).message}`;
    }
  }
}
